import * as React from 'react';
import { useContext } from 'react';
import { ServiceSummaryOptionsType, ServiceOptionItem, ServiceSummaryType } from '../../ServiceSummary/types';
import { getPassengerValuesMap } from '../utils';
import { CartContext } from '../Cart';
import { Notification, Person } from '../../Icons';
import ServiceSummary from '../../ServiceSummary';
import ServiceSummaryOption from '../../ServiceSummary/components/ServiceSummaryOption';
import { useTranslation } from 'react-i18next';
import { CartServiceProps } from '../types';
import { TravellerFieldEnum } from '@websky/graphql';
import ServiceSummaryDivider from '../../ServiceSummary/components/ServiceSummaryDivider/ServiceSummaryDivider';
import { convertGenderToCorrectValue, getUserValue } from '../../utils';
import { formatInvalidPassengerDate, hideText } from '../../Passenger/utils';
import { CheckoutSteps } from '../../MobileStepbar/types';
import { CdnIcon } from '@shared/ui';

const Passengers: React.FC<CartServiceProps> = ({
	travellers,
	showPassengers,
	customer,
	activeStep,
	steps,
	renderService
}) => {
	const cartContext = useContext(CartContext);

	const { t } = useTranslation('Cart'),
		{ t: passengerT } = useTranslation('Passenger');

	const items = React.useMemo<ServiceSummaryOptionsType[]>(() => {
		return travellers.map<ServiceSummaryOptionsType>(traveller => {
			const valuesMap = getPassengerValuesMap(traveller);

			const docType = passengerT(valuesMap.get(TravellerFieldEnum.DocType));
			const docNumber = hideText(valuesMap.get(TravellerFieldEnum.DocNumber));

			const gender = valuesMap.get(TravellerFieldEnum.Gender);
			const birthDate = formatInvalidPassengerDate(
				TravellerFieldEnum.BirthDate,
				valuesMap.get(TravellerFieldEnum.BirthDate)
			);

			const loyalty = valuesMap.get(TravellerFieldEnum.LoyaltyNumber);

			const main = [
				t(traveller.type),
				gender ? passengerT(convertGenderToCorrectValue(gender)) : null,
				birthDate
			].filter(Boolean);

			const documents = [docType, docNumber].filter(Boolean);
			const loyaltyInfo = [passengerT('loyaltyName'), loyalty];

			const travellerValues: ServiceSummaryOptionsType = {
				header: cartContext.travellersById.get(traveller.id).name,
				headerIcon: Person,
				items: [
					{ id: `main_${traveller.id}`, name: main.join(' • ') },
					{ id: `document_${traveller.id}`, name: documents.join(' • ') }
				]
			};

			if (loyalty) {
				travellerValues.items.push({
					id: `loyalty_${traveller.id}`,
					name: loyaltyInfo.join(' • ')
				});
			}

			return travellerValues;
		});
	}, [travellers]);

	const customerItems = React.useMemo<ServiceSummaryOptionsType>(() => {
		if (customer?.values) {
			const email = getUserValue(customer, TravellerFieldEnum.Email),
				phone = getUserValue(customer, TravellerFieldEnum.Phone);

			const items: ServiceOptionItem[] = [];

			if (email) {
				items.push({
					id: 'email',
					name: email
				});
			}

			if (phone) {
				items.push({
					id: 'phone',
					name: phone
				});
			}

			return {
				header: t('Contacts'),
				headerIcon: Notification,
				items
			};
		}

		return null;
	}, [customer]);

	const group: ServiceSummaryType = {
		header: t('Passengers'),
		icon: <CdnIcon icon={'Info/Passenger'} />
	};

	const content = (
		<>
			<ServiceSummaryDivider>
				{items.map((item, index) => (
					<ServiceSummaryOption groupOption={item} key={index} />
				))}
			</ServiceSummaryDivider>

			{customerItems && (
				<ServiceSummaryDivider>
					<ServiceSummaryOption groupOption={customerItems} />
				</ServiceSummaryDivider>
			)}
		</>
	);

	if (!showPassengers) {
		return null;
	}

	if (renderService) {
		return renderService(CheckoutSteps.Passengers, group, content);
	}

	return <ServiceSummary group={group}>{content}</ServiceSummary>;
};

export default Passengers;
